import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCallback } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug);
  }, [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (!article) {
    return (
      <main className="pt-16 min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-search-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-foreground-500 mb-6">您要查看的文章不存在或已被移除</p>
          <Link
            to="/articles"
            onClick={scrollToTop}
            className="px-5 py-2.5 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            返回建站学院
          </Link>
        </div>
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '易建站',
      url: 'https://yijianzhan.com',
    },
  };

  return (
    <>
      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '建站学院', path: '/articles' },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-10">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}阅读
                </span>
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full aspect-[2/1] object-cover object-top"
              />
            </div>

            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-background-200/70">
              <Link
                to="/articles"
                onClick={scrollToTop}
                className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回建站学院
              </Link>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14">
                <h3 className="text-xl font-heading text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      onClick={scrollToTop}
                      className="group p-4 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/9] rounded-md overflow-hidden mb-3">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h4 className="text-sm font-heading text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </>
  );
}